import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const { login, isAuthenticated, isBootstrapping } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isBootstrapping && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Login gagal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div
        className="panel w-full max-w-md p-6 sm:p-8 flex flex-col gap-5"
        data-testid="login-page"
      >
        <div>
          <span className="label-uppercase">
            PUSHNOW<span className="text-magenta"> DASHBOARD</span>
          </span>
          <h1 className="font-display font-black text-2xl mt-2 tracking-tight">Sign in</h1>
          <p className="text-sm text-text-secondary font-mono mt-1">
            PushNow IAM — dashboard requires an active session.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="label-micro">Email</span>
            <input
              type="email"
              data-testid="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="px-3 py-2 border border-border bg-surface text-foreground font-mono text-sm"
              style={{ borderRadius: 2 }}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="label-micro">Password</span>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2 pr-10 border border-border bg-surface text-foreground font-mono text-sm"
                style={{ borderRadius: 2 }}
              />
              <button
                type="button"
                data-testid="login-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-secondary hover:text-foreground"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Eye className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </label>

          {error && (
            <p
              className="text-xs font-mono text-magenta border border-magenta px-3 py-2"
              style={{ borderRadius: 2 }}
              data-testid="login-error"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={submitting || isBootstrapping}
            className="btn-magenta w-full justify-center py-2.5"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
